import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Users, GraduationCap, School, DollarSign, TrendingUp, Calendar } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className="text-3xl font-outfit font-bold text-slate-800">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/admin');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div>
        <h2 className="font-outfit font-semibold text-3xl text-slate-800 mb-2">
          Good Morning, {user?.name}! 👋
        </h2>
        <p className="text-slate-500">Here's what's happening with your school today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          testId="stat-students"
          icon={Users}
          label="Total Students"
          value={stats?.totalStudents || 0}
          color="bg-gradient-to-br from-indigo-500 to-indigo-600"
        />
        <StatCard
          testId="stat-teachers"
          icon={GraduationCap}
          label="Total Teachers"
          value={stats?.totalTeachers || 0}
          color="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          testId="stat-classes"
          icon={School}
          label="Total Classes"
          value={stats?.totalClasses || 0}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          testId="stat-fees"
          icon={DollarSign}
          label="Fees Collected"
          value={`₹${stats?.totalFeeCollected?.toLocaleString() || 0}`}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Upcoming Exams */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-5 h-5 text-indigo-600" />
          <h3 className="font-outfit font-semibold text-xl text-slate-800">Upcoming Exams</h3>
        </div>
        {stats?.upcomingExams?.length > 0 ? (
          <div className="space-y-3">
            {stats.upcomingExams.map((exam) => (
              <div key={exam.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div>
                  <p className="font-medium text-slate-800">{exam.examName}</p>
                  <p className="text-sm text-slate-500">{exam.startDate} - {exam.endDate}</p>
                </div>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                  Upcoming
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">No upcoming exams</p>
        )}
      </div>
    </div>
  );
}
